import React, { useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EntryCard from '../components/EntryCard';
import TipsterLogo from '../components/TipsterLogo';
import { PlanChip } from '../components/Pricing';
import { useData } from '../context/DataContext';
import { summarize, recentForm, monthKey, monthLabel } from '../lib/tips';

export default function TipsterPage() {
  const { id } = useParams();
  const { tipsters, entriesFor, loading } = useData();
  const tipster = tipsters.find(t => t.id === id);
  const list = useMemo(() => (tipster ? entriesFor(tipster.id) : []), [tipster, entriesFor]);
  const months = useMemo(() => [...new Set(list.map(e => monthKey(e.date)))], [list]);
  const [month, setMonth] = useState('all');

  if (!loading && !tipster) return <Navigate to="/" replace />;
  if (!tipster) return null;

  const visible = month === 'all' ? list : list.filter(e => monthKey(e.date) === month);
  const s = summarize(visible);
  const form = recentForm(list, 10);

  return (
    <>
      <Header />
      <section className="relative bg-navy text-white py-16 md:py-24 overflow-hidden grain" data-testid={`tipster-page-${tipster.id}`}>
        <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-30" style={{ background: tipster.accent }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-5">
              <TipsterLogo tipster={tipster} className="w-20 h-20" />
              <div>
                <div className="eyebrow" style={{ color: tipster.accent }}>{tipster.formatLabel}</div>
                <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">{tipster.name}</h1>
              </div>
            </div>
            <p className="mt-6 font-display text-xl md:text-2xl font-semibold text-white/90">{tipster.tagline}</p>
            <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">{tipster.description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {tipster.markets.map(m => <span key={m} className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-xs font-semibold">{m}</span>)}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/[0.07] border border-white/10 p-6 backdrop-blur" data-testid="tipster-stats">
              <div className="grid grid-cols-3 gap-3">
                {tipster.stats.map(st => (
                  <div key={st.label}>
                    <div className="font-mono text-base font-bold leading-tight">{st.value}</div>
                    <div className="text-[11px] text-slate-300 mt-1 leading-tight">{st.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-white/50">Record</div>
                  <div className="font-mono text-2xl font-bold">{summarize(list).won}W – {summarize(list).lost}L</div>
                </div>
                <div className="flex gap-1">{form.map((r, j) => <span key={j} className={`w-2.5 h-2.5 rounded-sm ${r === 'WIN' ? 'bg-win' : 'bg-loss'}`} />)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow text-royal">How {tipster.name} works</div>
            <ul className="mt-5 space-y-4">
              {tipster.approach.map((a, i) => (
                <li key={i} className="flex gap-4 rounded-2xl border border-line p-5 text-sm md:text-[15px] text-slate-700 leading-relaxed">
                  <span className="font-mono text-royal text-xs mt-0.5">0{i + 1}</span>{a}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-line p-6 lg:sticky lg:top-28" data-testid="tipster-plans">
              <div className="eyebrow text-slate-500">Subscribe to {tipster.name}</div>
              <div className="mt-5 grid gap-3 pt-2">
                {tipster.plans.map(p => <PlanChip key={p.days} tipster={tipster} plan={p} />)}
              </div>
              <Link to={`/subscribe/${tipster.id}`} className="btn-primary w-full mt-5" data-testid="tipster-subscribe-cta">Request access <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-mist">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="eyebrow text-royal">Archive</div>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-navy tracking-tight">Every release on the record.</h2>
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="tipster-kpis">
              {[['Released', s.released], ['Won', s.won], ['Lost', s.lost], ['Avg odds', s.avgOdds]].map(([l, v]) => (
                <div key={l} className="rounded-2xl bg-white border border-line px-4 py-3 text-center"><div className="font-mono text-xl font-bold text-navy">{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
              ))}
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="month-filter">
            <button onClick={() => setMonth('all')} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${month === 'all' ? 'bg-navy text-white border-navy' : 'bg-white text-slate-600 border-line hover:border-royal'}`} data-testid="month-all">All</button>
            {months.map(m => <button key={m} onClick={() => setMonth(m)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${month === m ? 'bg-navy text-white border-navy' : 'bg-white text-slate-600 border-line hover:border-royal'}`} data-testid={`month-${m}`}>{monthLabel(m)}</button>)}
          </div>
          <div className="mt-6 grid gap-3" data-testid="archive-list">
            {visible.map(e => <EntryCard key={e.id} entry={e} />)}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
